import { useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";

const Pagination = ({ pagination, totalPages, path, filters = "" }) => {
  const navigate = useNavigate();

  const handlePageChange = (value) => {
    if (value === "next") {
      navigate(`${path}?page=${pagination + 1}${filters}`);
    } else if (value === "prev") {
      navigate(`${path}?page=${pagination - 1}${filters}`);
    } else {
      navigate(`${path}?page=${value}${filters}`);
    }
  };

  const getPageNumbers = () => {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    } else if (pagination <= 2) {
      return [1, 2, 3, 4, 5];
    } else if (pagination >= totalPages - 1) {
      return [
        totalPages - 4,
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }
    const startPage = pagination - 2;
    const endPage = pagination + 2;
    return Array.from(
      { length: endPage - startPage + 1 },
      (_, index) => startPage + index,
    );
  };

  if (!pagination || totalPages <= 0) return null;

  const pageNumbers = getPageNumbers();

  return (
    <div className="flex flex-col items-center justify-between gap-3 px-2 py-3 sm:flex-row">
      {/* Current Page Summary */}
      <span className="text-xs text-gray-500 sm:text-sm">
        Page <span className="font-semibold text-gray-900">{pagination}</span>{" "}
        of <span className="font-semibold text-gray-900">{totalPages}</span>
      </span>

      {/* Pagination Controls */}
      <div className="flex items-center gap-1.5">
        {/* Previous Button */}
        <button
          disabled={pagination <= 1}
          onClick={() => handlePageChange("prev")}
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-45"
          aria-label="Previous Page"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        {/* Page Number Pills */}
        <div className="flex items-center gap-1">
          {pageNumbers.map((pageNumber) => {
            const isActive = pagination === pageNumber;
            return (
              <button
                key={pageNumber}
                onClick={() => handlePageChange(pageNumber)}
                type="button"
                className={`flex h-8 min-w-[32px] items-center justify-center rounded-lg px-2 text-xs font-medium transition-all ${
                  isActive
                    ? "bg-primary text-white shadow-sm shadow-indigo-100"
                    : "border border-transparent text-gray-600 hover:border-gray-200 hover:bg-gray-50 hover:text-gray-900"
                }`}
              >
                {pageNumber}
              </button>
            );
          })}
        </div>

        {/* Next Button */}
        <button
          disabled={pagination >= totalPages}
          onClick={() => handlePageChange("next")}
          type="button"
          className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-45"
          aria-label="Next Page"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default Pagination;
